export const isCsvFile = (file: File): boolean => file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');

const MIN_REQUIRED_COLUMNS = 9;

type CsvContentReadResult = {
  csvContent: string;
  isInvalidFormat: boolean;
};

const getFirstNonEmptyLine = (csvContent: string): string | undefined => {
  return csvContent
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find((line) => line.length > 0);
};

const readFileContentAsText = async (file: File): Promise<CsvContentReadResult> => {
  const csvContent = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string) || '');
    reader.onerror = () => reject(reader.error || new Error('Failed to read file content'));
    reader.readAsText(file);
  });

  const firstNonEmptyLine = getFirstNonEmptyLine(csvContent);
  const isHeaderInvalid = !firstNonEmptyLine || firstNonEmptyLine.split(',').length < MIN_REQUIRED_COLUMNS;
  const recordCount = countCsvDataRecords(csvContent);

  return {
    csvContent,
    isInvalidFormat: isHeaderInvalid || recordCount <= 1,
  };
};

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const validateImportFile = async (file?: File): Promise<string | undefined> => {
  // 1) Check file existence
  if (!file) {
    return 'contract.setting.import.validation.required';
  }

  // 2) Check file type
  if (!isCsvFile(file)) {
    return 'contract.setting.import.validation.invalidType';
  }

  // 3) Check basic file validity
  if (file.size <= 0) {
    return 'contract.setting.import.validation.invalidFormat';
  }

  // 4) Check file size limit
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return 'contract.setting.import.validation.fileTooLarge';
  }

  // 4) Check CSV content format and data
  // const { isInvalidFormat } = await readFileContentAsText(file);
  // if (isInvalidFormat) {
  //   return 'contract.setting.import.validation.invalidFormat';
  // }

  return undefined;
};

const hasAnyCellValue = (line: string): boolean => {
  return line.split(',').some((cell) => cell.trim().length > 0);
};

export const countCsvDataRecords = (csvContent: string): number => {
  const lines = csvContent
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .filter(hasAnyCellValue);

  if (lines.length <= 1) {
    return 0;
  }

  // First non-empty line is treated as header.
  return lines.length - 1;
};
